'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { useTenantOptional } from '@/contexts/TenantContext';
import { VISITOR_ID_COOKIE, STORAGE_KEYS } from '@/lib/attribution';

/**
 * Pushes a `page_context` event to the GTM dataLayer on every page navigation.
 * Provides tenant metadata, visitor identity, and UTM context that performance
 * marketers need for audience building, tag firing rules, and conversion
 * attribution in GTM.
 */
export function DataLayerEnrichment() {
  const tenant = useTenantOptional();
  const pathname = usePathname();
  const lastPathname = useRef<string | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined' || !tenant) return;
    if (pathname === lastPathname.current) return;
    lastPathname.current = pathname;

    const visitorId = getCookie(VISITOR_ID_COOKIE);
    const userId = getSupabaseUserId();
    const utmData = getSessionUtm();
    const emailCampaign = getSessionEmailCampaign();
    const clickIds = getSessionClickIds();
    const gaClientId = getCookie('_ga')?.split('.').slice(2).join('.') || undefined;
    const landingPage = sessionStorage.getItem(STORAGE_KEYS.LANDING_PAGE) ?? undefined;
    const referrer = sessionStorage.getItem(STORAGE_KEYS.REFERRER) ?? undefined;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'page_context',
      // Tenant context
      tenant_id: tenant.organisationId,
      site_id: tenant.siteId,
      theme_id: tenant.themeId,
      brand_name: tenant.branding?.brandName,
      locale: tenant.locale,
      // Page context
      page_path: pathname,
      page_type: derivePageType(pathname),
      // Visitor identity
      ...(visitorId && { visitor_id: visitorId }),
      ...(userId && { user_id: userId, is_authenticated: true }),
      ...(!userId && { is_authenticated: false }),
      ...(gaClientId && { ga_client_id: gaClientId }),
      // Attribution context
      ...(utmData.utm_source && { utm_source: utmData.utm_source }),
      ...(utmData.utm_medium && { utm_medium: utmData.utm_medium }),
      ...(utmData.utm_campaign && { utm_campaign: utmData.utm_campaign }),
      ...(utmData.utm_content && { utm_content: utmData.utm_content }),
      ...(utmData.utm_term && { utm_term: utmData.utm_term }),
      ...(landingPage && { landing_page: landingPage }),
      ...(referrer && { referrer }),
      // Click IDs
      ...(clickIds?.gclid && { gclid: clickIds.gclid }),
      ...(clickIds?.fbclid && { fbclid: clickIds.fbclid }),
      ...(clickIds?.msclkid && { msclkid: clickIds.msclkid }),
      ...(clickIds?.ttclid && { ttclid: clickIds.ttclid }),
      ...(clickIds?.li_fat_id && { li_fat_id: clickIds.li_fat_id }),
      ...(clickIds?.wbraid && { wbraid: clickIds.wbraid }),
      ...(clickIds?.gbraid && { gbraid: clickIds.gbraid }),
      ...(clickIds?.twclid && { twclid: clickIds.twclid }),
      // Email campaign context
      ...(emailCampaign?.eid && { email_id: emailCampaign.eid }),
      ...(emailCampaign?.email_campaign_id && { email_campaign_id: emailCampaign.email_campaign_id }),
    });
  }, [pathname, tenant]);

  return null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function derivePageType(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean);
  const path = segments.length > 0 && segments[0]!.length === 2 ? segments.slice(1) : segments;

  if (path.length === 0) return 'homepage';
  if (path[0] === 'properties' && path.length > 1) return 'property_detail';
  if (path[0] === 'properties') return 'property_listing';
  if (path[0] === 'projects' && path.length > 1) return 'project_detail';
  if (path[0] === 'projects') return 'project_listing';
  if (path[0] === 'news' || path[0] === 'blog') return path.length > 1 ? 'article' : 'blog_listing';
  if (path[0] === 'contact') return 'contact';
  if (path[0] === 'about') return 'about';
  return 'other';
}

function getCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match?.[1] ? decodeURIComponent(match[1]) : undefined;
}

function getSupabaseUserId(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('sb-') && key.endsWith('-auth-token')) {
        const raw = localStorage.getItem(key);
        if (raw) {
          const parsed = JSON.parse(raw);
          return parsed?.user?.id ?? null;
        }
      }
    }
  } catch {
    // Ignore
  }
  return null;
}

function getSessionUtm(): Record<string, string> {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEYS.UTM_LAST_TOUCH);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function getSessionEmailCampaign(): Record<string, string> | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEYS.EMAIL_CAMPAIGN);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function getSessionClickIds(): Record<string, string> | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEYS.CLICK_IDS);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}
